/** @jsxImportSource @emotion/react */
import { propertyIdPresenter } from '@kaiizen/kai-presenters';
import type { ApiPropertyProject } from '@kaiizen/kai-pro-api';
import {
  useAuth,
  useIsOffline,
  useKaiProApiClient,
  useMarketsByUserId,
  useOrgMarkets,
  usePropertyProjects,
} from '@kaiizen/kai-pro-api-react-state';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDebounce } from 'use-debounce';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { CaretDownIcon } from '@/Client/Icons/kaiShikiIconAliases';
import { PageLayout } from '@/Client/Layout';
import { usePageTitle, usePersistedState, usePropertyIdLabel } from '@/Client/Lib';
import { NoRecordsPlaceholder } from '@/Client/Partials';
import { useConfirmationModal, useProjectTypes, useToast } from '@/Client/State';
import { FilterIcon } from '@kaiizen/kai-shiki/react';
import { AddMarketsFlyout } from './AddMarketsFlyout';
import {
  getDateNumber,
  SearchCloseIcon,
  SearchIcon,
} from './dashboardHelpers';
import { type DashboardSortOption } from './DashboardSortDropdown';
import { type DashboardView, type SortMode } from './dashboardTypes';
import {
  getTurnBasedDashboardArtifactPresentation,
  getTurnBasedDashboardViewCounts,
  isTurnBasedDashboardProjectPastDue,
  projectMatchesTurnBasedDashboardListView,
  projectMatchesTurnBasedDashboardWorkView,
  type TurnBasedDashboardArtifact,
  type TurnBasedDashboardStatus,
  type TurnBasedDashboardWorkView,
} from './turnBasedDashboardLib';
import { TURN_BASED_DASHBOARD_PAGE_STYLES as styles } from './turnBasedDashboardPageStyles';
import { TurnBasedDashboardProjectRow } from './TurnBasedDashboardProjectRow';

const ALL_MARKETS_ID = 'all';
const INITIAL_PAGE_SIZE = 25;
const TURN_BASED_DASHBOARD_QUERY_LIMIT = 500;

const normalizeDashboardSortText = (value?: string | null) => {
  const normalizedValue = value?.trim() ?? '';
  return normalizedValue === '--' ? '' : normalizedValue;
};

const compareDashboardText = (left?: string | null, right?: string | null) => {
  const normalizedLeft = normalizeDashboardSortText(left);
  const normalizedRight = normalizeDashboardSortText(right);

  if (!normalizedLeft && !normalizedRight) return 0;
  if (!normalizedLeft) return 1;
  if (!normalizedRight) return -1;

  return normalizedLeft.localeCompare(normalizedRight, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const getTurnBasedDashboardSortLabel = (
  sortMode: SortMode,
  sortOptions: readonly DashboardSortOption[],
) => sortOptions.find(option => option.mode === sortMode)?.label ?? 'Last Updated';

/** Renders the compact sort control used by the turn-based dashboard toolbar. */
const TurnBasedDashboardSortDropdown = ({
  isOpen,
  sortMode,
  sortOptions,
  onToggle,
  onSelect,
}: {
  isOpen: boolean;
  sortMode: SortMode;
  sortOptions: readonly DashboardSortOption[];
  onToggle: () => void;
  onSelect: (mode: SortMode) => void;
}) => (
  <>
    <button
      type='button'
      css={styles.sortButton}
      aria-expanded={isOpen}
      aria-haspopup='menu'
      onClick={onToggle}
    >
      <span>{`Sort: ${getTurnBasedDashboardSortLabel(sortMode, sortOptions)}`}</span>
      <span css={[styles.dropdownCaret, isOpen && styles.dropdownCaretExpanded]}>
        <CaretDownIcon size={12} aria-hidden />
      </span>
    </button>
    {isOpen && (
      <div css={styles.sortMenu} role='menu' aria-label='Sort projects'>
        {sortOptions.map(option => {
          const isSelected = option.mode === sortMode;

          return (
            <button
              key={option.mode}
              type='button'
              role='menuitemradio'
              aria-checked={isSelected}
              css={[styles.sortMenuOption, isSelected && styles.sortMenuOptionSelected]}
              onClick={() => onSelect(option.mode)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    )}
  </>
);

/** Compares two project rows using the selected dashboard sort convention. */
const compareTurnBasedDashboardProjects = ({
  left,
  right,
  marketNameById,
  sortMode,
  userId,
}: {
  left: ApiPropertyProject;
  right: ApiPropertyProject;
  marketNameById: ReadonlyMap<string, string>;
  sortMode: SortMode;
  userId?: string;
}) => {
  if (sortMode === 'myTurn' && userId) {
    const turnDelta = Number(right.responsibleUserId === userId) - Number(left.responsibleUserId === userId);
    if (turnDelta !== 0) return turnDelta;
  }
  if (sortMode === 'address') return compareDashboardText(left.orgProperty?.address, right.orgProperty?.address);
  if (sortMode === 'orgPropertyId') return compareDashboardText(propertyIdPresenter(left.orgProperty), propertyIdPresenter(right.orgProperty));
  if (sortMode === 'market') {
    return compareDashboardText(
      marketNameById.get(left.orgProperty?.orgMarketId ?? ''),
      marketNameById.get(right.orgProperty?.orgMarketId ?? ''),
    );
  }
  if (sortMode === 'projectDueDate') {
    return getDateNumber(left.latestScan?.customerMetadata?.dueDate ?? left.firstScan?.customerMetadata?.dueDate)
      - getDateNumber(right.latestScan?.customerMetadata?.dueDate ?? right.firstScan?.customerMetadata?.dueDate);
  }
  return getDateNumber(right.updatedAt) - getDateNumber(left.updatedAt);
};

/** Supplies the three compact filter groups shown in the flagged dashboard popover. */
const TurnBasedDashboardFilters = ({
  projects,
  projectTypeOptions,
  selectedArtifact,
  selectedProjectType,
  selectedStatus,
  onSelectArtifact,
  onSelectProjectType,
  onSelectStatus,
  onClear,
}: {
  projects: readonly ApiPropertyProject[];
  projectTypeOptions: readonly { value: string; label: string }[];
  selectedArtifact?: TurnBasedDashboardArtifact;
  selectedProjectType?: string;
  selectedStatus?: TurnBasedDashboardStatus;
  onSelectArtifact: (artifact: TurnBasedDashboardArtifact) => void;
  onSelectProjectType: (projectType: string) => void;
  onSelectStatus: (status: TurnBasedDashboardStatus) => void;
  onClear: () => void;
}) => {
  const artifactOptions: readonly { value: TurnBasedDashboardArtifact; label: string }[] = [
    { value: 'scope', label: 'Scope' },
    { value: 'changeOrder', label: 'Change Order' },
    { value: 'punchList', label: 'Punch List' },
    { value: 'closeOut', label: 'Close Out' },
  ];
  const statusOptions: readonly { value: TurnBasedDashboardStatus; label: string }[] = [
    { value: 'approved', label: 'Approved' },
    { value: 'inProgress', label: 'In Progress' },
  ];
  const activeFilterCount = Number(!!selectedProjectType)
    + Number(!!selectedArtifact)
    + Number(!!selectedStatus);
  const countProjects = (predicate: (project: ApiPropertyProject) => boolean) => projects.filter(predicate).length;

  return (
    <div css={styles.filterMenu} role='dialog' aria-label='Dashboard filters'>
      <h2 css={styles.filterHeading}>Filters</h2>
      <div css={[styles.filterGroup, styles.filterGroupFirst]}>
        <div css={styles.filterGroupLabel}>Type</div>
        {projectTypeOptions.map(option => (
          <button
            key={option.value}
            type='button'
            css={[
              styles.filterOption,
              selectedProjectType === option.value && styles.filterOptionSelected,
            ]}
            aria-pressed={selectedProjectType === option.value}
            onClick={() => onSelectProjectType(option.value)}
          >
            <span>{option.label}</span>
            <span css={styles.filterCount}>
              {countProjects(project => project.customerMetadata?.projectType === option.value)}
            </span>
          </button>
        ))}
      </div>
      <div css={styles.filterGroup}>
        <div css={styles.filterGroupLabel}>Artifact</div>
        {artifactOptions.map(option => (
          <button
            key={option.value}
            type='button'
            css={[
              styles.filterOption,
              selectedArtifact === option.value && styles.filterOptionSelected,
            ]}
            aria-pressed={selectedArtifact === option.value}
            onClick={() => onSelectArtifact(option.value)}
          >
            <span>{option.label}</span>
            <span css={styles.filterCount}>
              {countProjects(project => getTurnBasedDashboardArtifactPresentation(project).artifact === option.value)}
            </span>
          </button>
        ))}
      </div>
      <div css={styles.filterGroup}>
        <div css={styles.filterGroupLabel}>Status</div>
        {statusOptions.map(option => (
          <button
            key={option.value}
            type='button'
            css={[
              styles.filterOption,
              selectedStatus === option.value && styles.filterOptionSelected,
            ]}
            aria-pressed={selectedStatus === option.value}
            onClick={() => onSelectStatus(option.value)}
          >
            <span css={styles.filterStatusLabel}>
              <span
                css={[
                  styles.filterStatusDot,
                  option.value === 'approved'
                    ? styles.filterStatusDotApproved
                    : styles.filterStatusDotInProgress,
                ]}
                aria-hidden
              />
              {option.label}
            </span>
            <span css={styles.filterCount}>
              {countProjects(project => getTurnBasedDashboardArtifactPresentation(project).status === option.value)}
            </span>
          </button>
        ))}
      </div>
      <div css={styles.filterFooter}>
        <span>{`${projects.length} ${projects.length === 1 ? 'job' : 'jobs'}`}</span>
        <button
          type='button'
          css={styles.clearFilterButton}
          disabled={activeFilterCount === 0}
          onClick={onClear}
        >
          Clear
        </button>
      </div>
    </div>
  );
};

/** The closer visual dashboard surface used by organizations in the turn-based handoff pilot. */
export const TurnBasedDashboardPage = () => {
  usePageTitle('Dashboard');
  const navigate = useNavigate();
  const { id: routeMarketId } = useParams();
  const { user, userCan, role, userPermissions } = useAuth();
  const kaiProApiClient = useKaiProApiClient();
  const propertyIdLabel = usePropertyIdLabel();
  const { getProjectTypeOptions } = useProjectTypes();
  const {
    markets: allOrgMarkets,
    isLoading: isLoadingAllOrgMarkets,
    marketsMutators,
  } = useOrgMarkets();
  const {
    markets,
    isLoading: isLoadingMarkets,
    refetch: refetchAssignedMarkets,
  } = useMarketsByUserId(user?.id);
  const { showToast, tryWithErrorToast } = useToast();
  const confirm = useConfirmationModal();
  const isOffline = useIsOffline();
  const isApprover = userCan('approve', 'Scan');
  const canManageDashboardMarkets = userCan('update', 'OrgMarket');
  const canViewAllTeams = role === 'admin' || role === 'manager';
  const showWorkViews = isApprover || role === 'fieldUser' || canViewAllTeams;
  const includeOtherAssignedProjectHolders = role === 'fieldUser';
  const isDashboardScopeReady = userPermissions !== undefined;
  const [dashboardView, setDashboardView] = usePersistedState<DashboardView>('turn-based-dashboard:view', 'active');
  const [workView, setWorkView] = usePersistedState<TurnBasedDashboardWorkView>(
    'turn-based-dashboard:work-view',
    showWorkViews ? 'myWork' : 'allJobs',
  );
  const [sortMode, setSortMode] = usePersistedState<SortMode>('turn-based-dashboard:sort-mode', 'myTurn');
  const [visibleCount, setVisibleCount] = useState(INITIAL_PAGE_SIZE);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAddMarketsOpen, setIsAddMarketsOpen] = useState(false);
  const [isSavingMarkets, setIsSavingMarkets] = useState(false);
  const [searchValue, setSearchValue] = usePersistedState('turn-based-dashboard:search', '');
  const [selectedProjectType, setSelectedProjectType] = useState<string>();
  const [selectedArtifact, setSelectedArtifact] = useState<TurnBasedDashboardArtifact>();
  const [selectedStatus, setSelectedStatus] = useState<TurnBasedDashboardStatus>();
  const [actionProjectId, setActionProjectId] = useState<string>();
  const [debouncedSearch] = useDebounce(searchValue.trim(), 250);
  const sortRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const selectedMarketId = routeMarketId ?? ALL_MARKETS_ID;
  const shouldShowExpandedSearch = isSearchOpen || !!searchValue.trim();
  const assignedMarketIds = useMemo(() => (markets ?? []).map(market => market.id), [markets]);
  const sortedAvailableMarkets = useMemo(
    () =>
      [...(allOrgMarkets ?? [])]
        .filter(market => !market.archivedAt)
        .sort((left, right) => left.name.localeCompare(right.name)),
    [allOrgMarkets],
  );
  const sortedMarkets = useMemo(
    () => sortedAvailableMarkets.filter(market => assignedMarketIds.includes(market.id)),
    [assignedMarketIds, sortedAvailableMarkets],
  );
  // The previous "Anyone in the team" view became "Your team". Keep its
  // saved value there; All teams is a distinct organization-wide view.
  const effectiveWorkView = showWorkViews
    ? workView === 'allJobs' || (workView === 'allTeams' && !canViewAllTeams) ? 'myTeam' : workView
    : 'allJobs';
  const isViewingAllTeams = canViewAllTeams && effectiveWorkView === 'allTeams';
  const dashboardMarkets = isViewingAllTeams ? sortedAvailableMarkets : sortedMarkets;
  const selectedOrgMarketIds = useMemo(() => {
    if (selectedMarketId === ALL_MARKETS_ID) {
      return isViewingAllTeams ? undefined : assignedMarketIds;
    }
    return dashboardMarkets.some(market => market.id === selectedMarketId)
      ? [selectedMarketId]
      : [];
  }, [assignedMarketIds, dashboardMarkets, isViewingAllTeams, selectedMarketId]);
  const {
    propertyProjects,
    isLoading: isLoadingProjects,
    propertyProjectMutators,
  } = usePropertyProjects({
    limit: TURN_BASED_DASHBOARD_QUERY_LIMIT,
    orgMarketIds: includeOtherAssignedProjectHolders ? undefined : selectedOrgMarketIds,
    assignedToUserId: role === 'fieldUser' ? user?.id : undefined,
    search: debouncedSearch || undefined,
    includeArchived: true,
    enabled: !!markets,
  });
  const projectsSafe = useMemo(() => propertyProjects ?? [], [propertyProjects]);

  useEffect(() => {
    if (!markets || routeMarketId) return;
    navigate(`/dashboard/market/${ALL_MARKETS_ID}`, { replace: true });
  }, [markets, navigate, routeMarketId]);

  useEffect(() => {
    if (!markets || selectedMarketId === ALL_MARKETS_ID) return;
    if (isViewingAllTeams && !allOrgMarkets) return;
    if (!dashboardMarkets.some(market => market.id === selectedMarketId)) {
      navigate(`/dashboard/market/${ALL_MARKETS_ID}`, { replace: true });
    }
  }, [allOrgMarkets, dashboardMarkets, isViewingAllTeams, markets, navigate, selectedMarketId]);

  useEffect(() => {
    setVisibleCount(INITIAL_PAGE_SIZE);
  }, [dashboardView, debouncedSearch, selectedArtifact, selectedMarketId, selectedProjectType, selectedStatus, sortMode, workView]);

  useEffect(() => {
    const closeMenus = (event: MouseEvent) => {
      if (!sortRef.current?.contains(event.target as Node)) setIsSortOpen(false);
      if (!filterRef.current?.contains(event.target as Node)) setIsFilterOpen(false);
    };

    if (isSortOpen || isFilterOpen) document.addEventListener('mousedown', closeMenus);
    return () => document.removeEventListener('mousedown', closeMenus);
  }, [isFilterOpen, isSortOpen]);

  const selectedMarket = useMemo(
    () => dashboardMarkets.find(market => market.id === selectedMarketId),
    [dashboardMarkets, selectedMarketId],
  );
  const searchPlaceholder = selectedMarketId === ALL_MARKETS_ID
    ? 'Search all markets'
    : `Search ${selectedMarket?.name ?? 'market'}`;
  const marketNameById = useMemo(
    () => new Map((includeOtherAssignedProjectHolders || isViewingAllTeams ? sortedAvailableMarkets : sortedMarkets).map(market => [market.id, market.name])),
    [includeOtherAssignedProjectHolders, isViewingAllTeams, sortedAvailableMarkets, sortedMarkets],
  );
  const activeCountByMarketId = useMemo(
    () =>
      new Map(dashboardMarkets.map(market => [
        market.id,
        includeOtherAssignedProjectHolders
          ? projectsSafe.filter(project =>
            project.orgProperty?.orgMarketId === market.id
            && projectMatchesTurnBasedDashboardListView(project, 'active')
          ).length
          : market.activeProjectCount ?? 0,
      ])),
    [dashboardMarkets, includeOtherAssignedProjectHolders, projectsSafe],
  );
  const viewCounts = useMemo(
    () => getTurnBasedDashboardViewCounts({ projects: projectsSafe, userId: user?.id, workView: effectiveWorkView, includeOtherAssignedProjectHolders }),
    [effectiveWorkView, projectsSafe, user?.id, includeOtherAssignedProjectHolders],
  );
  const { active: activeCount, archived: archivedCount, closed: completedCount } = viewCounts;

  const projectsForSelectedMarket = useMemo(
    () =>
      (includeOtherAssignedProjectHolders || isViewingAllTeams) && selectedMarketId !== ALL_MARKETS_ID
        ? projectsSafe.filter(project => project.orgProperty?.orgMarketId === selectedMarketId)
        : projectsSafe,
    [includeOtherAssignedProjectHolders, isViewingAllTeams, projectsSafe, selectedMarketId],
  );
  const projectsForCurrentView = useMemo(() => {
    if (debouncedSearch) return projectsForSelectedMarket;
    return projectsForSelectedMarket.filter(project => projectMatchesTurnBasedDashboardListView(project, dashboardView));
  }, [dashboardView, debouncedSearch, projectsForSelectedMarket]);
  const workScopedProjects = useMemo(
    () =>
      projectsForCurrentView.filter(project =>
        projectMatchesTurnBasedDashboardWorkView(project, user?.id, effectiveWorkView, { includeOtherAssignedProjectHolders })
      ),
    [effectiveWorkView, projectsForCurrentView, user?.id, includeOtherAssignedProjectHolders],
  );
  const projectTypeOptions = useMemo(
    () => getProjectTypeOptions(),
    [getProjectTypeOptions],
  );
  const filteredProjects = useMemo(
    () =>
      workScopedProjects.filter(project => {
        const artifactPresentation = getTurnBasedDashboardArtifactPresentation(project);
        return (!selectedProjectType || project.customerMetadata?.projectType === selectedProjectType)
          && (!selectedArtifact || artifactPresentation.artifact === selectedArtifact)
          && (!selectedStatus || artifactPresentation.status === selectedStatus);
      }),
    [selectedArtifact, selectedProjectType, selectedStatus, workScopedProjects],
  );
  const currentProjects = useMemo(
    () =>
      [...filteredProjects].sort((left, right) =>
        compareTurnBasedDashboardProjects({
          left,
          right,
          marketNameById,
          sortMode,
          userId: user?.id,
        })
      ),
    [filteredProjects, marketNameById, sortMode, user?.id],
  );
  const visibleProjects = currentProjects.slice(0, visibleCount);
  const myWorkCount =
    projectsForCurrentView.filter(project => projectMatchesTurnBasedDashboardWorkView(project, user?.id, 'myWork', { includeOtherAssignedProjectHolders }))
      .length;
  const myTeamCount =
    projectsForCurrentView.filter(project => projectMatchesTurnBasedDashboardWorkView(project, user?.id, 'myTeam', { includeOtherAssignedProjectHolders }))
      .length;
  const allTeamsCount = projectsForCurrentView.filter(project => projectMatchesTurnBasedDashboardWorkView(project, user?.id, 'allTeams')).length;
  const allMarketsActiveProjectCount = includeOtherAssignedProjectHolders
    ? projectsSafe.filter(project => projectMatchesTurnBasedDashboardListView(project, 'active')).length
    : activeCountByMarketId.size === 0
    ? undefined
    : Array.from(activeCountByMarketId.values()).reduce((total, count) => total + count, 0);
  const pastDueCount = workScopedProjects.filter(isTurnBasedDashboardProjectPastDue).length;
  const canLoadMore = currentProjects.length > visibleCount;
  const nextLoadCount = Math.min(INITIAL_PAGE_SIZE, currentProjects.length - visibleCount);
  const isLoading = isLoadingMarkets || isLoadingAllOrgMarkets || isLoadingProjects;
  const sortOptions: DashboardSortOption[] = [
    { mode: 'myTurn', label: 'Your Turn' },
    { mode: 'address', label: 'Address' },
    { mode: 'projectDueDate', label: 'Due Date' },
    { mode: 'lastUpdated', label: 'Last Updated' },
    { mode: 'market', label: 'Market Name' },
    { mode: 'orgPropertyId', label: 'Org Property ID' },
  ];

  const selectProjectType = (projectType: string) => {
    setSelectedProjectType(currentProjectType => currentProjectType === projectType ? undefined : projectType);
  };
  const selectArtifact = (artifact: TurnBasedDashboardArtifact) => {
    setSelectedArtifact(currentArtifact => currentArtifact === artifact ? undefined : artifact);
  };
  const selectStatus = (status: TurnBasedDashboardStatus) => {
    setSelectedStatus(currentStatus => currentStatus === status ? undefined : status);
  };
  const clearFilters = () => {
    setSelectedProjectType(undefined);
    setSelectedArtifact(undefined);
    setSelectedStatus(undefined);
  };
  const setProjectDashboardView = (view: DashboardView) => {
    setDashboardView(view);
    if (view !== 'active' && sortMode === 'myTurn') setSortMode('lastUpdated');
  };
  const archiveProject = (project: ApiPropertyProject) => {
    confirm({
      title: 'Archive Project',
      message: 'Are you sure you want to archive this project?',
      confirmLabel: 'Archive',
      onConfirm: async () => {
        setActionProjectId(project.id);
        await tryWithErrorToast('archiving project', async () => {
          await propertyProjectMutators.update(project.id, { archivedAt: new Date().toISOString() });
          trackEvent(ANALYTICS_EVENTS.projectArchived, { projectId: project.id });
        });
        await marketsMutators.refetchAllMarkets();
        setActionProjectId(undefined);
      },
    });
  };
  const reactivateProject = async (project: ApiPropertyProject) => {
    setActionProjectId(project.id);
    await tryWithErrorToast('reactivating project', async () => {
      await propertyProjectMutators.update(project.id, { status: 'active' });
      trackEvent(ANALYTICS_EVENTS.projectUpdated, { projectId: project.id, field: 'reactivated' });
    });
    await marketsMutators.refetchAllMarkets();
    setActionProjectId(undefined);
  };
  const unarchiveProject = async (project: ApiPropertyProject) => {
    setActionProjectId(project.id);
    await tryWithErrorToast('unarchiving project', async () => {
      await propertyProjectMutators.update(project.id, { archivedAt: null });
      trackEvent(ANALYTICS_EVENTS.projectUnarchived, { projectId: project.id });
    });
    await marketsMutators.refetchAllMarkets();
    setActionProjectId(undefined);
  };
  const saveAssignedMarkets = async (marketIds: string[]) => {
    if (!user?.id) return;

    const marketsToAdd = marketIds.filter(marketId => !assignedMarketIds.includes(marketId));
    const marketsToRemove = assignedMarketIds.filter(marketId => !marketIds.includes(marketId));
    if (marketsToAdd.length === 0 && marketsToRemove.length === 0) {
      setIsAddMarketsOpen(false);
      return;
    }

    setIsSavingMarkets(true);
    let didSucceed = false;
    await tryWithErrorToast('updating dashboard markets', async () => {
      await Promise.all([
        ...marketsToAdd.map(marketId => kaiProApiClient.addUserToOrgMarket(marketId, user.id)),
        ...marketsToRemove.map(marketId => kaiProApiClient.removeUserFromOrgMarket(marketId, user.id)),
      ]);
      await refetchAssignedMarkets();
      didSucceed = true;
    });
    setIsSavingMarkets(false);

    if (!didSucceed) return;

    showToast('Dashboard markets updated', 'success');
    setIsAddMarketsOpen(false);
  };
  if (!isDashboardScopeReady) {
    return (
      <PageLayout title='Dashboard' contentLayoutStyling='none'>
        <div css={styles.emptyState}>Loading dashboard...</div>
      </PageLayout>
    );
  }
  if (!isLoading && !isLoadingAllOrgMarkets && sortedAvailableMarkets.length === 0) {
    return (
      <PageLayout title='Dashboard' contentLayoutStyling='none'>
        <NoRecordsPlaceholder
          recordType='Markets'
          description='Start by creating your first market.'
          createButtonText='Create Market'
          createFirstRecord={() => navigate('/market/new')}
          iconType='dashboard'
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout title='Dashboard' contentLayoutStyling='none'>
      <div css={styles.page}>
        {canManageDashboardMarkets && (
          <AddMarketsFlyout
            open={isAddMarketsOpen}
            availableMarkets={sortedAvailableMarkets}
            initialSelectedMarketIds={assignedMarketIds}
            isSaving={isSavingMarkets}
            onClose={() => setIsAddMarketsOpen(false)}
            onSave={saveAssignedMarkets}
          />
        )}
        <aside css={styles.sidebar}>
          {showWorkViews && (
            <>
              <div css={styles.sidebarTitle}>Assigned to</div>
              <div css={styles.sidebarWorkList}>
                {([
                  ['myWork', 'You', myWorkCount],
                  ['myTeam', 'Your team', myTeamCount],
                  ...(canViewAllTeams ? [['allTeams', 'All teams', allTeamsCount] as const] : []),
                ] as const).map(([value, label, count]) => (
                  <button
                    key={value}
                    type='button'
                    css={[
                      styles.sidebarItem,
                      effectiveWorkView === value && styles.sidebarItemSelected,
                    ]}
                    onClick={() => setWorkView(value)}
                  >
                    <span css={styles.sidebarItemName}>{label}</span>
                    <span css={styles.sidebarItemCount}>{count}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          <div css={[styles.sidebarTitle, showWorkViews && styles.sidebarTitleAfterWork]}>Markets</div>
          <div css={styles.sidebarMarketList}>
            <button
              type='button'
              css={[styles.sidebarItem, selectedMarketId === ALL_MARKETS_ID && styles.sidebarItemSelected]}
              onClick={() => navigate(`/dashboard/market/${ALL_MARKETS_ID}`)}
            >
              <span css={styles.sidebarItemName}>All markets</span>
              <span css={styles.sidebarItemCount}>
                {allMarketsActiveProjectCount ?? '--'}
              </span>
            </button>
            {dashboardMarkets.map(market => {
              const isSelected = selectedMarketId === market.id;
              const activeProjectCount = activeCountByMarketId.get(market.id) ?? 0;
              return (
                <button
                  key={market.id}
                  type='button'
                  css={[styles.sidebarItem, isSelected && styles.sidebarItemSelected]}
                  onClick={() => navigate(`/dashboard/market/${market.id}`)}
                >
                  <span css={styles.sidebarItemName}>{market.name}</span>
                  <span css={styles.sidebarItemCount}>{activeProjectCount || '--'}</span>
                </button>
              );
            })}
          </div>
          {canManageDashboardMarkets && (
            <div css={styles.sidebarFooter}>
              <button type='button' css={styles.addMarketButton} onClick={() => setIsAddMarketsOpen(true)}>
                + Add Market
              </button>
            </div>
          )}
        </aside>

        <main css={styles.main}>
          <div css={styles.mainContent}>
            {isOffline && <div css={styles.emptyState}>Offline mode: dashboard data may be stale.</div>}
            <div css={styles.toolbar}>
              <div css={styles.toolbarPrimary}>
                <div css={styles.projectViewTabs}>
                  {([
                    ['active', 'Active', activeCount],
                    ['closed', 'Completed', completedCount],
                    ['archived', 'Archived', archivedCount],
                  ] as const).map(([value, label, count], index) => (
                    <button
                      key={value}
                      type='button'
                      css={[
                        styles.projectViewTab,
                        index > 0 && styles.projectViewTabConnected,
                        dashboardView === value && styles.projectViewTabSelected,
                      ]}
                      onClick={() => setProjectDashboardView(value)}
                    >
                      <span>{label}</span>
                      <span>{count}</span>
                    </button>
                  ))}
                </div>
                <div css={styles.filterMenuRoot} ref={filterRef}>
                  <button
                    type='button'
                    css={[styles.filterButton, isFilterOpen && styles.filterButtonSelected]}
                    aria-expanded={isFilterOpen}
                    onClick={() => setIsFilterOpen(open => !open)}
                  >
                    <FilterIcon size={14} aria-hidden />
                    Filters
                    <span css={[styles.dropdownCaret, isFilterOpen && styles.dropdownCaretExpanded]}>
                      <CaretDownIcon size={12} aria-hidden />
                    </span>
                  </button>
                  {isFilterOpen && (
                    <TurnBasedDashboardFilters
                      projects={workScopedProjects}
                      projectTypeOptions={projectTypeOptions}
                      selectedArtifact={selectedArtifact}
                      selectedProjectType={selectedProjectType}
                      selectedStatus={selectedStatus}
                      onSelectArtifact={selectArtifact}
                      onSelectProjectType={selectProjectType}
                      onSelectStatus={selectStatus}
                      onClear={clearFilters}
                    />
                  )}
                </div>
                {pastDueCount > 0 && <span css={styles.pastDue}>{`${pastDueCount} past due`}</span>}
              </div>

              <div css={styles.toolbarActions}>
                <div css={styles.sort} ref={sortRef}>
                  <TurnBasedDashboardSortDropdown
                    isOpen={isSortOpen}
                    sortMode={sortMode}
                    sortOptions={sortOptions}
                    onToggle={() => setIsSortOpen(open => !open)}
                    onSelect={mode => {
                      setSortMode(mode);
                      setIsSortOpen(false);
                    }}
                  />
                </div>
                {shouldShowExpandedSearch
                  ? (
                    <div css={styles.search}>
                      <span css={styles.searchIcon}>
                        <SearchIcon />
                      </span>
                      <input
                        autoFocus
                        css={styles.searchInput}
                        type='search'
                        aria-label='Search projects'
                        value={searchValue}
                        placeholder={searchPlaceholder}
                        onChange={event => setSearchValue(event.target.value)}
                        onBlur={() => {
                          if (!searchValue.trim()) setIsSearchOpen(false);
                        }}
                      />
                      {!!searchValue.trim() && (
                        <button
                          type='button'
                          css={styles.searchClearButton}
                          aria-label='Clear search'
                          onClick={() => {
                            setSearchValue('');
                            setIsSearchOpen(false);
                          }}
                        >
                          <SearchCloseIcon />
                        </button>
                      )}
                    </div>
                  )
                  : (
                    <button
                      type='button'
                      css={styles.searchButton}
                      aria-label='Open search'
                      onClick={() => setIsSearchOpen(true)}
                    >
                      <SearchIcon />
                    </button>
                  )}
              </div>
            </div>

            {isLoading
              ? <div css={styles.emptyState}>Loading dashboard...</div>
              : visibleProjects.length === 0
              ? <div css={styles.emptyState}>{debouncedSearch ? `No projects match “${debouncedSearch}”.` : 'No projects found.'}</div>
              : (
                <>
                  <div css={styles.projectList}>
                    {visibleProjects.map(project => (
                      <TurnBasedDashboardProjectRow
                        key={project.id}
                        project={project}
                        marketName={marketNameById.get(project.orgProperty?.orgMarketId ?? '')}
                        propertyIdLabel={propertyIdLabel}
                        userId={user?.id}
                        onArchive={archiveProject}
                        onUnarchive={unarchiveProject}
                        onReactivate={reactivateProject}
                        actionInFlight={actionProjectId === project.id}
                        onClick={() => navigate(`/project/${project.id}`)}
                      />
                    ))}
                  </div>
                  {canLoadMore && (
                    <button
                      type='button'
                      css={styles.loadMore}
                      onClick={() => setVisibleCount(count => count + INITIAL_PAGE_SIZE)}
                    >
                      {`Load ${nextLoadCount} More Projects`}
                    </button>
                  )}
                </>
              )}
          </div>
        </main>
      </div>
    </PageLayout>
  );
};
